import { type ThunkAction } from "redux-thunk";
import { type RootState } from "~/app/store";
import throttle from "lodash/throttle";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { ACTIVE_QUOTE_PAGE, CART_PAGE } from "~/utils/url/staticUrls";
import { getStore } from "~/app/store/getStore";
import { fetchMinicart } from "~/features/cart/minicart/fetchMinicart";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, MinicartReducerActions>;

export interface MinicartStore {
  show: boolean;
  count: number;
  total?: PriceData;
  url: string;
}

const initialState: MinicartStore = {
  show: window.SCHA.minicart?.show ?? false,
  count: window.SCHA.minicart?.count ?? 0,
  total: window.SCHA.minicart?.total,
  url: userIsSalesman() ? ACTIVE_QUOTE_PAGE : CART_PAGE,
};

const REFRESH_MINICART = "REFRESH_MINICART";

let promiseInFlight: Promise<unknown> | null = null;

export function refreshMinicartThunk(): ThunkResult<void> {
  return async (dispatch) => {
    if (!promiseInFlight) {
      const promise = fetchMinicart();
      promiseInFlight = promise;
      const data = await promise;
      promiseInFlight = null;
      dispatch(updateMinicartValues(data.miniCartCount, data.miniCartPrice));
    } else {
      promiseInFlight.then(() => window.SCHA.updateMinicart());
    }
  };
}

export function clearMinicartAction() {
  return updateMinicartValues(0);
}

function updateMinicartValues(count: number, total?: PriceData) {
  return {
    type: REFRESH_MINICART,
    payload: {
      count,
      total,
    },
  } as const;
}

export type MinicartReducerActions = ReturnType<typeof updateMinicartValues>;

export const minicartReducer = (state: MinicartStore = initialState, action: MinicartReducerActions): MinicartStore => {
  switch (action.type) {
    case REFRESH_MINICART: {
      const { count, total } = action.payload;
      return {
        ...state,
        count,
        total,
      };
    }

    default:
      return state;
  }
};

window.SCHA.clearMinicart = () => getStore().dispatch(clearMinicartAction());
window.SCHA.updateMinicart = throttle(() => getStore().dispatch(refreshMinicartThunk()), 2000);

export const selectMinicartData = (state: RootState) => state.minicart;
